import { configureStore } from '@reduxjs/toolkit'
import { combineReducers } from 'redux'
import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import sessionStorageEngine from 'redux-persist/lib/storage/session'
import thunk from 'redux-thunk'

import { application } from './application.reducer'
import { auth } from './auth.reducer'
import { borrower } from './borrower.reducer'
import { globalConfigReducer } from './global.config.reducer'
import { header } from './header.reducer'
import { loan } from './loan.reducer'
import { loanGlobalStatus } from './loan.status.reducer'
import { loanDetail } from './loanDetail.reducer'
import { ltvMaxLimit } from './ltvMaxLimit.reducer'
import pipelineReducer from './pipeline.reducer'
import { step } from './step.reducer'
import { vendor } from './vendor.reducer'

const reducer = combineReducers({
  auth,
  header,
  loan,
  step,
  loanDetail,
  application,
  borrower,
  ltvMaxLimit,
  vendor,
  loanGlobalStatus,
  globalConfigReducer,
  pipelineFilters: pipelineReducer,
})

const { href } = window.location
const isTemporaryUrl = href.length > 200 && !href.includes('/borrower')

const emptyStorage = {
  getItem: async () => '',
  setItem: async () => '',
  removeItem: async () => '',
}

/**
 * A "Log in as Borrower" window (Manage Borrowers) is a borrower session living in the same
 * browser, on the same origin, as the staff member's own session - and `localStorage` is shared
 * across every tab of an origin. Persisting that window to `localStorage` would overwrite
 * `persist:root`, so the staff member's next reload anywhere would come up as the borrower.
 *
 * So this window persists to `sessionStorage`, which is per-tab: its session survives a reload of
 * the portal window and is invisible to every other tab. The marker is latched into
 * `sessionStorage` on the way in because this config is read once per page load, and the portal
 * navigates off `/borrowerPortalAs/...` immediately - without the latch a reload deeper in the
 * portal would fall back to `localStorage` and pick the staff session back up.
 */
const VIEW_AS_BORROWER_KEY = 'nexysViewAsBorrower'
const isViewAsBorrowerWindow = (() => {
  const onEntryPath = href.includes('/borrowerPortalAs/')
  try {
    if (onEntryPath) window.sessionStorage.setItem(VIEW_AS_BORROWER_KEY, '1')
    return window.sessionStorage.getItem(VIEW_AS_BORROWER_KEY) === '1'
  } catch (e) {
    // Private mode or blocked site data: the entry path is still a reliable signal, and falling
    // back to a non-persisted store is the safe direction - it cannot clobber the staff session.
    return onEntryPath
  }
})()

const persistConfig = {
  key: 'root',
  version: 1,
  storage: isTemporaryUrl ? emptyStorage : isViewAsBorrowerWindow ? sessionStorageEngine : storage,
  transforms: [
    createTransform(
      (inbound) => inbound,
      (outbound: any) => ({ ...outbound, ReportPlaceholders: {} }),
      { whitelist: ['globalConfigReducer'] },
    ),
  ],
}

const persistedReducer = persistReducer(persistConfig, reducer)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(thunk),
})

export const persistor = persistStore(store)
